// widgets/chart/ui/ActiveColumnDual.tsx
import { FC, useEffect, useMemo, useRef, useState, useCallback } from "react";
import styled from "styled-components";
import { chartValue, LegendItem, PeriodKeyForFill, XDomain, XTickFormatter } from "widgets/chart/model/types";
import { ChartTooltip, TooltipData, TooltipTextItem } from "./ChartTooltip";
import { HOVER_LINE_COLOR } from "widgets/chart/ui/ActiveColumn.tsx";

const COLUMN_DUAL_ANIM_MS = 500;
const COLUMN_DUAL_STAGGER_MS = 35;
const TOOLTIP = { width: 167, height: 96, gap: 8 };
const TOOLTIP_FADE_MS = 150;

const TOKENS = {
    color: {
        bar1: "var(--accent-soft, rgba(255, 114, 65, 1))",
        bar1Last: "#FF4F12",
        bar2: "var(--neutral-muted, rgba(181, 183, 192, 1))",
        bar2Last: "#9AA0A6",
        grid: "var(--neutral-container-soft, rgba(88, 93, 105, 0.05)",
        axis: "rgba(0, 0, 0, 0)",
        marks: "#6B8AFF",
        threshold: "#FF4F12",
        text: "var(--fg-muted, rgba(16, 24, 40, 0.55))",
        hoverLine: HOVER_LINE_COLOR,
    },
    size: { padL: 8, padR: 12, padT: 8, padB: 24 },
    text: { fontSize: 10, fontWeight: 400 as const, lineHeight: 16, letterSpacing: 0.4 },
};

const Wrap = styled.div<{ w: string | number; h: number | string }>`
    position: relative;
    width: ${({ w }) => (typeof w === "number" ? `${w}px` : w)};
    height: ${({ h }) => (typeof h === "number" ? `${h}px` : h)};
    display: flex;
    flex-direction: column;
`;
const Stage = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`;
const Overlay = styled.div`
    pointer-events: none;
    position: absolute;
    inset: 0;
`;
const AxisLabel = styled.div`
    position: absolute;
    transform: translate(-50%, 0);
    color: ${TOKENS.color.text};
    font-size: ${TOKENS.text.fontSize}px;
    font-weight: ${TOKENS.text.fontWeight};
    letter-spacing: ${TOKENS.text.letterSpacing}px;
    line-height: ${TOKENS.text.lineHeight}px;
    white-space: nowrap;
    pointer-events: none;
    text-align: center;
`;
const AxisLabelY = styled(AxisLabel)`
    transform: translate(0, -50%);
    left: calc(100% - ${TOKENS.size.padR - 2}px);
`;
const FadeTooltip = styled.div<{ visible: boolean }>`
    opacity: ${({ visible }) => (visible ? 1 : 0)};
    transition: opacity ${TOOLTIP_FADE_MS}ms ease;
`;

function useMeasure() {
    const [node, setNode] = useState<HTMLDivElement | null>(null);
    const [rect, setRect] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
    const ref = useCallback((el: HTMLDivElement | null) => {
        setNode((prev) => (prev === el ? prev : el));
    }, []);
    useEffect(() => {
        if (!node) return;
        const ro = new ResizeObserver((entries) => {
            const r = entries[0].contentRect;
            setRect({ w: r.width, h: r.height });
        });
        ro.observe(node);
        const rr = node.getBoundingClientRect();
        setRect({ w: rr.width, h: rr.height });
        return () => ro.disconnect();
    }, [node]);
    return { ref, rect };
}
function inferXUnit(stepMs: number): "hour" | "day" | "week" | "month" {
    const h = 3600_000;
    const d = 24 * h;
    if (stepMs <= 2 * h) return "hour";
    if (stepMs <= 2 * d) return "day";
    if (stepMs <= 14 * d) return "week";
    return "month";
}
function periodMsByKey(k: PeriodKeyForFill): number {
    const h = 3600_000;
    const d = 24 * h;
    if (k === "day") return d;
    if (k === "week") return 7 * d;
    return 30 * d;
}
const ddmm = (d: Date) => `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
const hhmm = (d: Date) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
const isSameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

function placeTooltip(x: number, yTop: number, viewWidth: number) {
    const minLeft = 0;
    const maxLeft = Math.max(0, viewWidth - TOOLTIP.width);
    const rawLeft = x - TOOLTIP.width / 2;
    const left = Math.min(Math.max(rawLeft, minLeft), maxLeft);
    let arrowX = x - left;
    arrowX = Math.max(6, Math.min(TOOLTIP.width - 6, arrowX));
    const top = yTop - TOOLTIP.height - TOOLTIP.gap;
    return { left, top, arrowX };
}

type Props = {
    data: chartValue[];
    data2: chartValue[];
    seriesLabels: { first: string; second: string };
    width?: string | number;
    height?: number | string;
    legend?: LegendItem[];
    marks?: number[];
    thresholds?: number[];
    showAxes?: boolean;
    fillFuture?: boolean;
    periodForFill?: PeriodKeyForFill;
    highlightLast?: boolean;
    onLegend?: (items: LegendItem[]) => void;
    tooltipText: TooltipTextItem[];
    xTicks?: number[];
    xTickFormatter?: XTickFormatter;
    xDomain?: XDomain;
};

export const ActiveColumnDual: FC<Props> = ({
                                                data,
                                                data2,
                                                seriesLabels,
                                                width = "100%",
                                                height = "100%",
                                                legend,
                                                marks,
                                                thresholds,
                                                showAxes = true,
                                                fillFuture = false,
                                                periodForFill = "day",
                                                highlightLast = false,
                                                onLegend,
                                                tooltipText,
                                                xTicks: externalXTicks,
                                                xTickFormatter,
                                                xDomain,
                                            }) => {
    const meas = useMeasure();
    const svgRef = useRef<SVGSVGElement | null>(null);

    const vw = Math.max(1, meas.rect.w || 600);
    const vh = Math.max(1, meas.rect.h || (typeof height === "number" ? height : 240));

    const { padL, padR, padT, padB } = TOKENS.size;

    const real1 = data.filter((d) => d.y != null);
    const real2 = data2.filter((d) => d.y != null);

    const stepX =
        real1.length > 1 ? real1[1].x - real1[0].x : real2.length > 1 ? real2[1].x - real2[0].x : 3600_000;
    const xUnit = inferXUnit(stepX);

    const xsAll = [...real1, ...real2].map((d) => d.x);
    const minXData = xsAll.length ? Math.min(...xsAll) : 0;
    const maxXData = xsAll.length ? Math.max(...xsAll) : minXData;
    const desiredEnd = fillFuture ? minXData + periodMsByKey(periodForFill) : maxXData;
    const minXFixed = xDomain?.min ?? minXData;
    const maxXFixed = xDomain?.max ?? Math.max(maxXData + stepX, desiredEnd);

    const ysNum = [...real1, ...real2].map((d) => d.y as number);
    const avgY = ysNum.reduce((a, b) => a + b, 0) / (ysNum.length || 1);
    let minY = Math.min(...(ysNum.length ? ysNum : [0])) - avgY * 0.2;
    let maxY = Math.max(...(ysNum.length ? ysNum : [1])) + avgY * 0.2;
    if (!(isFinite(minY) && isFinite(maxY)) || minY === maxY) {
        minY = minY - 1;
        maxY = maxY + 1;
    }

    const baseInnerW = vw - padL - padR;
    const slots = Math.max(
        1,
        Math.round((maxXFixed - minXFixed) / stepX)
    );
    const groupW = Math.max(8, baseInnerW / (slots * 1.5));
    const barW = (groupW - 6) / 2;
    const padLx = padL;
    const padRx = padR;
    const innerW = vw - padLx - padRx;
    const barPadL = padL + groupW / 2;
    const barPadR = padR + groupW / 2;
    const barInnerW = vw - barPadL - barPadR;

    const xScale = (x: number) => padLx + ((x - minXFixed) / ((maxXFixed - minXFixed) || 1)) * innerW;
    const xScaleBar = (x: number) =>
        barPadL + ((x - minXFixed) / ((maxXFixed - minXFixed) || 1)) * barInnerW;
    const yScale = (y: number) => vh - padB - ((y - minY) / (maxY - minY || 1)) * (vh - padT - padB);

    const [enter, setEnter] = useState(false);
    const [visible, setVisible] = useState(false);
    const [tip, setTip] = useState<{ left: number; top: number; arrowX: number; xPix: number; data: TooltipData } | null>(null);

    useEffect(() => {
        setEnter(false);
        setTip(null);
        const id = requestAnimationFrame(() => setEnter(true));
        return () => cancelAnimationFrame(id);
    }, [JSON.stringify(real1.map((d) => d.y)), JSON.stringify(real2.map((d) => d.y)), real1.length, real2.length, fillFuture, periodForFill, vw, vh,]);

    const yTicks = useMemo(() => {
        const res: number[] = [];
        const step = (maxY - minY) / 5;
        for (let i = 0; i <= 5; i++) res.push(minY + i * step);
        return res;
    }, [minY, maxY]);

    const autoXTicks = useMemo(() => {
        const rangeMs = maxXFixed - minXFixed;
        const dayMs = 24 * 3600_000;
        const weekRangeMin = 6 * dayMs;
        const weekRangeMax = 8 * dayMs;
        const yearRangeMin = 300 * dayMs;
        const yearRangeMax = 400 * dayMs;
        
        const isWeekRange = rangeMs >= weekRangeMin && rangeMs <= weekRangeMax;
        const isYearRange = rangeMs >= yearRangeMin && rangeMs <= yearRangeMax;
        
        let tickCount = 5;
        if (isWeekRange) {
            tickCount = 7;
        } else if (isYearRange) {
            tickCount = 4;
        }
        
        const step = (maxXFixed - minXFixed) / (tickCount - 1 || 1);
        return Array.from({ length: tickCount }, (_, i) => minXFixed + i * step);
    }, [minXFixed, maxXFixed]);

    const xTicks = useMemo(() => {
        const ticks = externalXTicks && externalXTicks.length > 1 ? externalXTicks : autoXTicks;
        
        let isWeek = false;
        if (ticks.length > 1) {
            const stepMs = ticks[1] - ticks[0];
            const dayMs = 24 * 3600_000;
            const dayTolerance = 12 * 3600_000;
            isWeek = Math.abs(stepMs - dayMs) < dayTolerance;
        }
        
        return { ticks, isWeek };
    }, [externalXTicks, autoXTicks]);

    const fmtXParts = useCallback(
        (ms: number) => {
            const d = new Date(ms);
            const today = new Date();
            
            if (xTicks.ticks.length > 1) {
                const stepMs = xTicks.ticks[1] - xTicks.ticks[0];
                const quarterMs = 3 * 30 * 24 * 3600_000; 
                const tolerance = 10 * 24 * 3600_000; 
                const dayMs = 24 * 3600_000;
                const dayTolerance = 12 * 3600_000;
                
                if (Math.abs(stepMs - quarterMs) < tolerance || stepMs > quarterMs * 0.8) {
                    const quarter = Math.floor(d.getMonth() / 3) + 1;
                    const year = d.getFullYear();
                    return [`${quarter} квартал`, String(year)];
                }
   
                if (Math.abs(stepMs - dayMs) < dayTolerance) {
                    return [ddmm(d)];
                }
            }
            
            if (xUnit === "hour") {
                if (isSameDay(d, today)) return [hhmm(d)];
                return [`${hhmm(d)}`, ddmm(d)];
            }
            
            return [ddmm(d)];
        },
        [xUnit, xTicks]
    );
    const fmtY = (v: number) => {
        const range = Math.abs(maxY - minY);
        return range >= 10 ? Math.round(v).toString() : v.toFixed(1);
    };
    const formatTick = useCallback(
        (value: number, index: number, ticks: number[]) => {
            if (xTickFormatter) return xTickFormatter(value, index, ticks);
            return fmtXParts(value);
        },
        [xTickFormatter, fmtXParts]
    );

    const legendAuto: LegendItem[] = useMemo(() => {
        const seriesLegend: LegendItem[] = [
            { color: TOKENS.color.bar1, label: seriesLabels.first },
            { color: TOKENS.color.bar2, label: seriesLabels.second },
        ];
        const markItems = (marks ?? []).map((v) => ({ color: TOKENS.color.marks, label: `Метка ${v}` }));
        const extra = legend ?? [];
        return [...seriesLegend, ...markItems, ...extra];
    }, [seriesLabels, marks, legend]);

    useEffect(() => {
        if (onLegend) onLegend(legendAuto);
    }, [onLegend, legendAuto]);

    const nearestByX = (arr: chartValue[], x: number) => {
        if (!arr.length) return null;
        let best = 0;
        let bestD = Infinity;
        for (let i = 0; i < arr.length; i++) {
            const d = Math.abs(arr[i].x - x);
            if (d < bestD) {
                bestD = d;
                best = i;
            }
        }
        return arr[best];
    };

    const showTipAt = (cx: number, yTop: number, xTs: number) => {
        if (!showAxes) return;
        const pos = placeTooltip(cx, yTop, vw);
        const p1 = nearestByX(real1, xTs);
        const p2 = nearestByX(real2, xTs);
        setTip({
            left: pos.left,
            top: pos.top,
            arrowX: pos.arrowX,
            xPix: cx,
            data: {
                type: "column2",
                current1: { ts: p1?.x ?? xTs, value: (p1?.y as number) ?? null },
                current2: { ts: p2?.x ?? xTs, value: (p2?.y as number) ?? null },
                tooltipText,
            },
        });
        setVisible(true);
    };

    const showTipAtX = (xp: number) => {
        if (!showAxes || (real1.length === 0 && real2.length === 0)) return;
        const rect = (svgRef.current as SVGSVGElement).getBoundingClientRect();
        const relX = xp - rect.left;
        const xVal = ((relX - padLx) / (innerW || 1)) * (maxXFixed - minXFixed) + minXFixed;
        const p1 = nearestByX(real1, xVal);
        const p2 = nearestByX(real2, xVal);
        const base = p1 ?? p2;
        if (!base) return;
        const cx = xScaleBar(base.x);
        const yTop = Math.min(
            yScale(Math.max(minY, (p1?.y as number) ?? minY)),
            yScale(Math.max(minY, (p2?.y as number) ?? minY))
        );
        showTipAt(cx, yTop, base.x);
    };

    const onSvgMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
        showTipAtX(e.clientX);
    };

    const onSvgMouseLeave = () => {
        setVisible(false);
        setTimeout(() => setTip(null), TOOLTIP_FADE_MS);
    };

    const visibleMarks = useMemo(() => (marks ?? []).filter((m) => m >= minY && m <= maxY), [marks, minY, maxY]);
    const visibleThresholds = useMemo(
        () => (thresholds ?? []).filter((t) => t >= minY && t <= maxY),
        [thresholds, minY, maxY]
    );

    return (
        <Wrap w={width} h={height}>
            <Stage ref={meas.ref}>
                <svg
                    ref={svgRef}
                    width={vw}
                    height={vh}
                    style={{ display: "block" }}
                    onMouseMove={onSvgMouseMove}
                    onMouseLeave={onSvgMouseLeave}
                >
                    <rect x={0} y={0} width={vw} height={vh} fill="transparent" />

                    {showAxes && (
                        <>
                            <line x1={padLx} x2={vw - padRx} y1={vh - padB} y2={vh - padB} stroke={TOKENS.color.axis} strokeWidth={1} />
                            {yTicks.map((t, i) => (
                                <line key={`yg-${i}`} x1={padLx} x2={vw - padRx} y1={yScale(t)} y2={yScale(t)} stroke={TOKENS.color.grid} strokeWidth={1} />
                            ))}
                            {xTicks.ticks.map((t, i) => {
                                const isLast = i === xTicks.ticks.length - 1;
                                if (isLast && !xTicks.isWeek) return null;
                                const x = xScale(t);
                                return <line key={`xg-${i}`} x1={x} x2={x} y1={padT} y2={vh - padB} stroke={TOKENS.color.grid} strokeWidth={1} />;
                            })}
                        </>
                    )}

                    {real1.map((d, i) => {
                        const cx = xScaleBar(d.x);
                        const x1 = cx - groupW / 2;
                        const y0 = yScale(minY);
                        const y1 = yScale(Math.max(minY, d.y as number));
                        const isLast = i === real1.length - 1;
                        const color = highlightLast && isLast ? TOKENS.color.bar1Last : TOKENS.color.bar1;
                        const h = Math.abs(y0 - y1);
                        const opacity = highlightLast ? (isLast ? 1 : 0.4) : 1;
                        const center = x1 + barW / 2;
                        const top = Math.min(y1, y0);
                        return (
                            <rect
                                key={`g1-${i}`}
                                x={x1}
                                y={Math.min(y1, y0)}
                                width={barW}
                                height={h}
                                fill={color}
                                opacity={opacity}
                                style={{
                                    transformBox: "fill-box",
                                    transformOrigin: "center bottom",
                                    transform: enter ? "scaleY(1)" : "scaleY(0)",
                                    transition: `transform ${COLUMN_DUAL_ANIM_MS}ms ease ${i * COLUMN_DUAL_STAGGER_MS}ms, opacity 160ms ease`,
                                    cursor: showAxes ? "pointer" : "default",
                                }}
                                onMouseEnter={() => showAxes && showTipAt(center, top, d.x)}
                            />
                        );
                    })}

                    {real2.map((d, i) => {
                        const cx = xScaleBar(d.x);
                        const x2 = cx - groupW / 2 + barW + 6;
                        const y0 = yScale(minY);
                        const y2 = yScale(Math.max(minY, d.y as number));
                        const isLast = i === real2.length - 1;
                        const color = highlightLast && isLast ? TOKENS.color.bar2Last : TOKENS.color.bar2;
                        const h = Math.abs(y0 - y2);
                        const opacity = highlightLast ? (isLast ? 1 : 0.4) : 1;
                        const center = x2 + barW / 2;
                        const top = Math.min(y2, y0);
                        return (
                            <rect
                                key={`g2-${i}`}
                                x={x2}
                                y={Math.min(y2, y0)}
                                width={barW}
                                height={h}
                                fill={color}
                                opacity={opacity}
                                style={{
                                    transformBox: "fill-box",
                                    transformOrigin: "center bottom",
                                    transform: enter ? "scaleY(1)" : "scaleY(0)",
                                    transition: `transform ${COLUMN_DUAL_ANIM_MS}ms ease ${i * COLUMN_DUAL_STAGGER_MS}ms, opacity 160ms ease`,
                                    cursor: showAxes ? "pointer" : "default",
                                }}
                                onMouseEnter={() => showAxes && showTipAt(center, top, d.x)}
                            />
                        );
                    })}

                    {visibleMarks.map((m, i) => (
                        <line key={`m-${i}`} x1={padLx} x2={vw - padRx} y1={yScale(m)} y2={yScale(m)} stroke={TOKENS.color.marks} strokeDasharray="6 6" strokeWidth={1} />
                    ))}
                    {visibleThresholds.map((t, i) => (
                        <line key={`thr-${i}`} x1={padLx} x2={vw - padRx} y1={yScale(t)} y2={yScale(t)} stroke={TOKENS.color.threshold} strokeWidth={1} />
                    ))}

                    {showAxes && tip && (
                        <line
                            x1={tip.xPix}
                            x2={tip.xPix}
                            y1={padT}
                            y2={vh - padB}
                            stroke={TOKENS.color.hoverLine}
                            strokeWidth={1}
                            strokeDasharray="4 2"
                            pointerEvents="none"
                        />
                    )}
                </svg>

                <Overlay>
                    {showAxes &&
                        yTicks.map((t, i) => (
                            <AxisLabelY key={`yl-${i}`} style={{ top: yScale(t) }}>
                                {fmtY(t)}
                            </AxisLabelY>
                        ))}
                    {showAxes &&
                        xTicks.ticks.map((t, i) => {
                            const parts = formatTick(t, i, xTicks.ticks);
                            const isLast = i === xTicks.ticks.length - 1;
                            return (
                                <AxisLabel key={`xl-${i}`} style={{ left: xScale(t), top: vh - padB + 2 }}>
                                    {isLast && !xTicks.isWeek ? "" : (
                                        <>
                                            {parts[0]}
                                            {parts[1] ? (
                                                <>
                                                    <br />
                                                    {parts[1]}
                                                </>
                                            ) : null}
                                        </>
                                    )}
                                </AxisLabel>
                            );
                        })}
                    {showAxes && tip && (
                        <FadeTooltip visible={visible}>
                            <ChartTooltip
                                data={tip.data}
                                tooltipText={tooltipText}
                                style={{ left: tip.left, top: tip.top, ["--arrow-x"]: `${tip.arrowX}px` }}
                            />
                        </FadeTooltip>
                    )}
                </Overlay>
            </Stage>
        </Wrap>
    );
};
